// A push image has to fit both platforms. Android refuses anything over 1 MB
// (the picture simply does not show); iOS allows far more but gains nothing
// from it. Photos straight off a phone run 3–8 MB, so rather than bounce them
// back at the admin, shrink to a JPEG that fits. A GIF keeps its animation
// only if left alone, so an oversized GIF is refused instead.

export const PUSH_IMAGE_MAX_BYTES = 1024 * 1024;
export const PUSH_IMAGE_MAX_DIMENSION = 1600;
// What iOS can attach. Anything else would show on Android and silently
// not on iPhones.
export const PUSH_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];

// Why a file cannot be a push image, in the admin's words — or null.
export function pushImageProblem(file, maxBytes = PUSH_IMAGE_MAX_BYTES) {
    if (!file) return 'Choose an image.';
    if (PUSH_IMAGE_TYPES.indexOf(file.type) === -1) {
        return 'Use a JPG, PNG or GIF. Other formats do not show on iPhones.';
    }
    if (file.type === 'image/gif' && file.size > maxBytes) {
        return 'A GIF has to be under 1 MB to show on Android, and shrinking one would lose its animation. Use a smaller GIF.';
    }
    return null;
}

// Nothing here may hang the screen: a decode that never settles would leave
// the loading overlay up for good, so every browser step has a deadline.
const STEP_TIMEOUT_MS = 20_000;

function withDeadline(promise, what) {
    let timer;
    const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${what} took too long. Use an image under 1 MB.`)), STEP_TIMEOUT_MS);
    });
    return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

function defaultLoadImage(file) {
    return withDeadline(new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That image could not be read.')); };
        img.src = url;
    }), 'Reading the image');
}

function defaultMakeCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

function toBlob(canvas, quality) {
    return withDeadline(new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality)), 'Shrinking the image');
}

// The ladder: full size at falling quality, then half the size, and so on.
// Every step is a real candidate; the first one under the cap wins.
const QUALITIES = [0.85, 0.75, 0.65, 0.55];
const MAX_HALVINGS = 3;

/**
 * Returns the file itself when it already fits, or a JPEG that does.
 * Throws with a message for the admin when it cannot be made to fit.
 *
 * `loadImage` and `makeCanvas` are injectable so the ladder can be tested
 * without a real canvas, which jsdom does not have.
 */
export async function fitImageForPush(file, {
    maxBytes = PUSH_IMAGE_MAX_BYTES,
    maxDimension = PUSH_IMAGE_MAX_DIMENSION,
    loadImage = defaultLoadImage,
    makeCanvas = defaultMakeCanvas
} = {}) {
    const problem = pushImageProblem(file, maxBytes);
    if (problem) throw new Error(problem);
    if (file.size <= maxBytes) return file;

    const img = await loadImage(file);
    // A decode that yields no pixels is not an image; a 1x1 push picture
    // would be worse than an error.
    if (!(img.width >= 2 && img.height >= 2)) throw new Error('That image could not be read.');
    const longest = Math.max(img.width, img.height);
    let scale = Math.min(1, maxDimension / longest);

    for (let halving = 0; halving <= MAX_HALVINGS; halving++) {
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));
        const canvas = makeCanvas(width, height);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('This browser cannot shrink images. Use an image under 1 MB.');
        // JPEG has no transparency; without this a transparent PNG comes out
        // on black. White is what a notification tray expects.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        for (const quality of QUALITIES) {
            const blob = await toBlob(canvas, quality);
            if (blob && blob.size > 0 && blob.size <= maxBytes) {
                const name = (file.name || 'push-image').replace(/\.[^.]+$/, '') + '.jpg';
                return new File([blob], name, { type: 'image/jpeg' });
            }
        }
        scale /= 2;
    }
    throw new Error('Could not shrink this image under 1 MB. Use a smaller one.');
}
